import { Users, Zap, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AvatarDisplay } from '@/components/AvatarDisplay';
import { useAuth } from '@/hooks/useAuth';
import { usePrivateRooms } from '@/hooks/usePrivateRooms';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export function FriendsListSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { friends, friendsPresence, quickChallengeFriend } = usePrivateRooms();

  // Filter for accepted friends only
  const acceptedFriends = friends.filter(f => f.status === 'accepted');

  // Get friend user IDs
  const friendIds = acceptedFriends.map(f => 
    f.requester_id === user?.id ? f.addressee_id : f.requester_id
  );

  // Fetch friend profiles
  const { profiles: friendProfiles, loading } = usePlayerProfiles(friendIds);

  const handleQuickChallenge = async (friendId: string, timeControl: string) => {
    try {
      await quickChallengeFriend(friendId, timeControl);
      toast.success(`Quick ${timeControl} challenge sent!`);
      navigate('/private-rooms');
    } catch (error) {
      toast.error('Failed to send challenge');
    }
  };

  const isOnline = (userId: string) => {
    return friendsPresence[userId]?.online || false;
  };

  if (loading) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <CardTitle>Your Friends</CardTitle>
        </div>
        <CardDescription>Challenge your friends to a quick game</CardDescription>
      </CardHeader>
      <CardContent>
        {acceptedFriends.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
            <p className="text-muted-foreground font-medium">No friends yet</p>
            <p className="text-sm text-muted-foreground mt-1">Search for players above to add friends</p>
          </div>
        ) : (
          <div className="space-y-3">
            {acceptedFriends.map(friendship => {
              const friendId = friendship.requester_id === user?.id 
                ? friendship.addressee_id 
                : friendship.requester_id;
              const profile = friendProfiles[friendId];

              if (!profile) return null;

              const online = isOnline(friendId);

              return (
                <div
                  key={friendship.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="relative">
                      <AvatarDisplay
                        avatarConfig={profile.avatar_config}
                        avatarUrl={profile.avatar_url}
                        username={profile.username || profile.display_name || 'Anonymous'}
                        size="md"
                      />
                      <div
                        className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-card ${
                          online ? 'bg-green-500' : 'bg-muted-foreground'
                        }`}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">
                        {profile.display_name || profile.username || 'Anonymous'}
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        {profile.username && <span>@{profile.username}</span>}
                        <span>•</span>
                        <span>{profile.elo_rating} ELO</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleQuickChallenge(friendId, '5+0')}
                      className="gap-1.5"
                    >
                      <Zap className="h-3.5 w-3.5" />
                      5+0
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleQuickChallenge(friendId, '10+0')}
                      className="gap-1.5"
                    >
                      <Clock className="h-3.5 w-3.5" />
                      10+0
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
